// dashboard/src/components/billing/SubscriptionSuccessModal.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  planCredits: number;
  bonusCredits?: number;
}

const SubscriptionSuccessModal: React.FC<SubscriptionSuccessModalProps> = ({
  isOpen,
  onClose,
  planName,
  planCredits,
  bonusCredits = 0
}) => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Auto-hide confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleCreateApiKey = () => {
    onClose();
    navigate('/dashboard/api-keys', { 
      state: { 
        showWelcome: true,
        fromSubscription: true 
      } 
    });
  };

  const handleGoToDashboard = () => {
    onClose();
    navigate('/dashboard');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
      
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-60">
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <span className="text-3xl">🎉</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to {planName}!
            </h2>
            <p className="text-gray-600">
              Your subscription is now active and ready to use.
            </p>
          </div>

          {/* Plan Details */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
            <div className="text-center">
              <h3 className="font-semibold text-blue-900 mb-3">Your Plan Includes:</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-blue-700">Monthly Credits:</span>
                  <span className="font-bold text-blue-900">{planCredits.toLocaleString()}</span>
                </div>
                {bonusCredits > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">Bonus Credits:</span>
                    <span className="font-bold text-green-900">+{bonusCredits.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700 font-medium">Total Available:</span>
                    <span className="font-bold text-blue-900 text-lg">
                      {(planCredits + bonusCredits).toLocaleString()} credits
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 text-center">Ready to Get Started?</h3>
            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-blue-500 mr-3">1️⃣</span>
                <span>Create your first API key</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-green-500 mr-3">2️⃣</span>
                <span>Test the API in our playground</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-purple-500 mr-3">3️⃣</span>
                <span>Start building amazing projects!</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleCreateApiKey}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              🚀 Create Your First API Key
            </button>
            
            <button
              onClick={handleGoToDashboard}
              className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`
        .confetti-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 3s linear infinite;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionSuccessModal;
